import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate, useLocation, Link } from 'react-router-dom';
import { UserPlus, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { usePageMeta } from '../hooks/usePageMeta';

/**
 * Client Self-Signup — invite-only.
 *
 * Contract: the endpoint accepts only emails an operator has already bound
 * via the Clients tab (POST /api/clients/{id}/access). Unbound emails and
 * already-registered emails are rejected without leaking DB state. On
 * success the account is completed in place (password set on the operator's
 * placeholder row) and the user lands on their dashboard.
 */
export const Signup = () => {
  usePageMeta({
    title: 'Sign Up — Epicurea Nexus',
    description: 'Complete your Epicurea Nexus account (invite required).',
  });
  const { user, signup } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (user) {
    return <Navigate to={location.state?.from || '/dashboard'} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await signup(email.trim().toLowerCase(), password, name.trim());
    } catch (err) {
      setError(err.response?.data?.detail || 'Sign-up failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{
        backgroundImage: "url('https://static.prod-images.emergentagent.com/jobs/ea9cf972-3434-4165-bf74-a93c34e7a810/images/d8d3b06a78b6c2e2bf5c7a86e3da4f10f8c5e9e6c3b8b4f5a1c3d7e9f2a4b6c8.png')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: '#F9F8F6',
      }}
      data-testid="signup-page"
    >
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/ea9cf972-3434-4165-bf74-a93c34e7a810/images/93589658ea558262a331193e658bbed9ba248c401b58657ed297db3b7130550f.png"
            alt="Epicurea Nexus"
            className="h-20 w-auto mb-4"
          />
          <h1 className="text-3xl font-bold text-primary tracking-tight">Epicurea Nexus</h1>
          <p className="text-sm text-text-muted tracking-[0.2em] uppercase mt-1">Client Portal · Sign Up</p>
        </div>

        <div className="bg-white border border-border rounded-md shadow-lg p-8">
          <div className="flex items-center space-x-2 mb-6">
            <UserPlus className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold text-primary">Create your account</h2>
          </div>

          <div className="bg-primary/5 border border-primary/20 rounded-md p-3 text-xs text-text-secondary mb-5 flex items-start gap-2">
            <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
            <span>
              Access is invite-only. Use the email address your Epicurea contact added on your restaurant record.
            </span>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-wider text-text-secondary font-semibold mb-1">
                Your name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
                placeholder="Aditi Sharma"
                required
                autoFocus
                data-testid="signup-name"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-text-secondary font-semibold mb-1">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
                placeholder="owner@yourrestaurant.com"
                required
                data-testid="signup-email"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-text-secondary font-semibold mb-1">
                Password (min 8 chars)
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
                required
                minLength={8}
                data-testid="signup-password"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-text-secondary font-semibold mb-1">
                Confirm password
              </label>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
                required
                minLength={8}
                data-testid="signup-confirm"
              />
            </div>

            {error && (
              <div className="bg-red-50 border-l-4 border-state-error rounded-md p-3 flex items-start space-x-2" data-testid="signup-error">
                <AlertCircle className="h-4 w-4 text-state-error flex-shrink-0 mt-0.5" />
                <p className="text-sm text-state-error">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-primary hover:bg-primary-hover text-white py-3"
              data-testid="signup-submit"
            >
              {submitting ? 'Creating account…' : 'Create account & sign in'}
            </Button>
          </form>

          <p className="text-xs text-text-muted text-center mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-primary font-semibold hover:underline" data-testid="signup-login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;
